import React, { useState } from "react";
import { ChevronDown, ChevronRight, Shield, Eye, Lock, Database, Globe, UserCheck, Settings, AlertCircle, CheckCircle, Clock, Mail, Download, Trash2, FileText, Cookie, Bell, Phone, MapPin, Scale } from "lucide-react";

const PrivacyPolicy = () => {
  const [expandedSections, setExpandedSections] = useState({ overview: true });
  const [lastUpdated] = useState("September 15, 2025");

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const sections = [
    {
      id: "overview",
      title: "1. Privacy Overview",
      icon: Shield,
      color: "text-green-600",
      content: (
        <div className="space-y-4 sm:space-y-6">
          <p className="text-base sm:text-lg text-gray-700">
            At Tuly.app, your privacy is fundamental to our mission. We've designed our AI workflow automation 
            platform with privacy-first principles, ensuring your data is protected while delivering powerful automation insights.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4 text-center">
              <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-green-600 mx-auto mb-2" />
              <h4 className="font-semibold text-green-800 text-sm sm:text-base">Data Protection</h4>
              <p className="text-xs sm:text-sm text-green-700 mt-1">Enterprise-grade security and encryption</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 text-center">
              <UserCheck className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600 mx-auto mb-2" />
              <h4 className="font-semibold text-blue-800 text-sm sm:text-base">Your Control</h4>
              <p className="text-xs sm:text-sm text-blue-700 mt-1">Full control over your data and preferences</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 sm:p-4 text-center sm:col-span-2 lg:col-span-1">
              <Eye className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600 mx-auto mb-2" />
              <h4 className="font-semibold text-purple-800 text-sm sm:text-base">Transparency</h4>
              <p className="text-xs sm:text-sm text-purple-700 mt-1">Clear, honest communication about data use</p>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-3 sm:p-4 rounded-r-lg">
            <p className="text-blue-800 text-sm sm:text-base">
              <strong>Quick Summary:</strong> We collect only the data necessary to provide our AI automation services, 
              never sell your personal information, and give you full control over your data.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "information-collection",
      title: "2. Information We Collect",
      icon: Database,
      color: "text-blue-600",
      content: (
        <div className="space-y-4 sm:space-y-6">
          <h4 className="font-semibold text-gray-900 text-base sm:text-lg">Types of Data We Collect</h4>
          
          <div className="space-y-3 sm:space-y-4">
            <div className="border rounded-lg p-3 sm:p-4">
              <h5 className="font-semibold text-blue-600 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
                <UserCheck className="w-4 h-4 sm:w-5 sm:h-5" />
                Account Information
              </h5>
              <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
                <li>• Name and email address (from OAuth providers like Google)</li>
                <li>• Profile picture and basic profile information</li>
                <li>• Account preferences and subscription details</li>
                <li>• Authentication tokens and session data</li>
              </ul>
            </div>

            <div className="border rounded-lg p-3 sm:p-4">
              <h5 className="font-semibold text-purple-600 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
                <Settings className="w-4 h-4 sm:w-5 sm:h-5" />
                Usage and Workflow Data
              </h5>
              <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
                <li>• Workflow descriptions and automation requests</li>
                <li>• Generated workflow recommendations and configurations</li>
                <li>• Tool selections and integration preferences</li>
                <li>• Budget estimates and ROI calculations</li>
                <li>• Usage patterns and feature interactions</li>
              </ul>
            </div>

            <div className="border rounded-lg p-3 sm:p-4">
              <h5 className="font-semibold text-orange-600 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
                <Globe className="w-4 h-4 sm:w-5 sm:h-5" />
                Technical Information
              </h5>
              <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
                <li>• IP address and general location (country/city level)</li>
                <li>• Browser type, version, and device information</li>
                <li>• Usage analytics and performance metrics</li>
                <li>• Error logs and diagnostic information</li>
                <li>• API usage and rate limiting data</li>
              </ul>
            </div>

            <div className="border rounded-lg p-3 sm:p-4">
              <h5 className="font-semibold text-green-600 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
                <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
                Communication Data
              </h5>
              <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
                <li>• Support conversations and feedback</li>
                <li>• Survey responses and user research participation</li>
                <li>• Email communications and preferences</li>
                <li>• Community forum posts (if applicable)</li>
              </ul>
            </div>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-500 p-3 sm:p-4 rounded-r-lg">
            <p className="text-amber-800 text-sm sm:text-base">
              <strong>Data Minimization:</strong> We collect only the minimum data necessary to provide our services. 
              You can always review and manage your data through your account settings.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "data-usage",
      title: "3. How We Use Your Information",
      icon: Settings,
      color: "text-purple-600",
      content: (
        <div className="space-y-4 sm:space-y-6">
          <h4 className="font-semibold text-gray-900 text-base sm:text-lg">Primary Uses</h4>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <h5 className="font-semibold text-green-600 mb-2 sm:mb-3 text-sm sm:text-base">Service Delivery</h5>
              <ul className="text-xs sm:text-sm text-gray-700 space-y-1 sm:space-y-2">
                <li>✓ Generate AI-powered workflow recommendations</li>
                <li>✓ Provide tool suggestions and integrations</li>
                <li>✓ Calculate budget estimates and ROI forecasts</li>
                <li>✓ Deliver risk assessments and implementation plans</li>
                <li>✓ Maintain your account and subscription</li>
                <li>✓ Process payments and billing</li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-blue-600 mb-2 sm:mb-3 text-sm sm:text-base">Service Improvement</h5>
              <ul className="text-xs sm:text-sm text-gray-700 space-y-1 sm:space-y-2">
                <li>✓ Analyze usage patterns to enhance AI accuracy</li>
                <li>✓ Improve platform performance and reliability</li>
                <li>✓ Develop new features and capabilities</li>
                <li>✓ Conduct security monitoring and fraud prevention</li>
                <li>✓ Provide customer support and technical assistance</li>
                <li>✓ Send service updates and important notifications</li>
              </ul>
            </div>
          </div>

          <h4 className="font-semibold text-gray-900 mt-4 sm:mt-6 text-base sm:text-lg">AI Training and Model Improvement</h4>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 sm:p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <h6 className="font-semibold text-green-600 mb-2 text-sm">What We Use</h6>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• Anonymized workflow patterns</li>
                  <li>• Aggregated usage statistics</li>
                  <li>• Performance metrics and outcomes</li>
                  <li>• General industry trends</li>
                </ul>
              </div>
              <div>
                <h6 className="font-semibold text-red-600 mb-2 text-sm">What We Never Use</h6>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• Personal identifying information</li>
                  <li>• Confidential business data</li>
                  <li>• Proprietary workflow details</li>
                  <li>• Client-specific information</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-3 sm:p-4 rounded-r-lg">
            <p className="text-blue-800 text-sm sm:text-base">
              <strong>Opt-Out Option:</strong> You can opt out of having your anonymized data used for AI model 
              improvements at any time through your privacy settings.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "data-sharing",
      title: "4. Information Sharing and Disclosure",
      icon: Globe,
      color: "text-cyan-600",
      content: (
        <div className="space-y-4 sm:space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
            <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2 text-sm sm:text-base">
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
              Our Commitment: We Never Sell Your Data
            </h4>
            <p className="text-green-700 text-xs sm:text-sm">
              Tuly.app has never sold, and will never sell, your personal information to third parties for marketing purposes.
            </p>
          </div>

          <h4 className="font-semibold text-gray-900 text-base sm:text-lg">Limited Sharing Scenarios</h4>
          <div className="space-y-3">
            <div className="border-l-4 border-blue-500 pl-3 sm:pl-4">
              <h5 className="font-semibold text-blue-700 text-sm sm:text-base">Service Providers</h5>
              <p className="text-xs sm:text-sm text-gray-700 mt-1">
                We share data with trusted service providers (hosting, payment processing, analytics) 
                who are contractually bound to protect your information.
              </p>
              <div className="text-xs text-gray-600 mt-1">
                Examples: AWS (hosting), Stripe (payments), Google Analytics (usage analytics)
              </div>
            </div>
            
            <div className="border-l-4 border-orange-500 pl-3 sm:pl-4">
              <h5 className="font-semibold text-orange-700 text-sm sm:text-base">Legal Requirements</h5>
              <p className="text-xs sm:text-sm text-gray-700 mt-1">
                We may disclose information when required by law, court order, or to protect our rights and users' safety.
              </p>
            </div>
            
            <div className="border-l-4 border-purple-500 pl-3 sm:pl-4">
              <h5 className="font-semibold text-purple-700 text-sm sm:text-base">Business Transfers</h5>
              <p className="text-xs sm:text-sm text-gray-700 mt-1">
                In the event of a merger, acquisition, or sale, your data may be transferred. 
                You will be notified of any such change.
              </p>
            </div>

            <div className="border-l-4 border-green-500 pl-3 sm:pl-4">
              <h5 className="font-semibold text-green-700 text-sm sm:text-base">With Your Consent</h5>
              <p className="text-xs sm:text-sm text-gray-700 mt-1">
                We may share information for other purposes with your explicit consent, 
                such as integrations you authorize.
              </p>
            </div>
          </div>

          <h4 className="font-semibold text-gray-900 mt-4 sm:mt-6 text-base sm:text-lg">Third-Party Integrations</h4>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
            <p className="text-yellow-800 text-xs sm:text-sm">
              <strong>Tool Recommendations:</strong> When we recommend third-party tools for your workflows, 
              we don't share your data with those providers unless you explicitly choose to integrate with them. 
              Each integration requires your separate consent and is governed by that provider's privacy policy.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "data-security",
      title: "5. Data Security and Protection",
      icon: Lock,
      color: "text-red-600",
      content: (
        <div className="space-y-4 sm:space-y-6">
          <h4 className="font-semibold text-gray-900 text-base sm:text-lg">Security Measures</h4>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
                <h5 className="font-semibold text-red-600 mb-2 text-sm sm:text-base">Data Encryption</h5>
                <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
                  <li>• AES-256 encryption at rest</li>
                  <li>• TLS 1.3 encryption in transit</li>
                  <li>• End-to-end encryption for sensitive data</li>
                  <li>• Regular encryption key rotation</li>
                </ul>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                <h5 className="font-semibold text-blue-600 mb-2 text-sm sm:text-base">Access Controls</h5>
                <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
                  <li>• Multi-factor authentication (MFA)</li>
                  <li>• Role-based access controls</li>
                  <li>• Principle of least privilege</li>
                  <li>• Regular access reviews</li>
                </ul>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
                <h5 className="font-semibold text-green-600 mb-2 text-sm sm:text-base">Infrastructure Security</h5>
                <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
                  <li>• SOC 2 Type II compliant hosting</li>
                  <li>• Regular security audits</li>
                  <li>• Intrusion detection systems</li>
                  <li>• DDoS protection and monitoring</li>
                </ul>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 sm:p-4">
                <h5 className="font-semibold text-purple-600 mb-2 text-sm sm:text-base">Operational Security</h5>
                <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
                  <li>• Security awareness training</li>
                  <li>• Incident response procedures</li>
                  <li>• Regular security assessments</li>
                  <li>• Vulnerability management program</li>
                </ul>
              </div>
            </div>
          </div>

          <h4 className="font-semibold text-gray-900 mt-4 sm:mt-6 text-base sm:text-lg">Data Breach Response</h4>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 sm:p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 mt-0.5 flex-shrink-0" />
              <div>
                <h5 className="font-semibold text-orange-800 mb-2 text-sm sm:text-base">Our Commitment</h5>
                <p className="text-orange-700 text-xs sm:text-sm">
                  In the unlikely event of a data breach, we will notify affected users within 72 hours 
                  and provide clear information about what happened, what data was involved, and what 
                  steps we're taking to resolve the issue.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4">
            <h5 className="font-semibold text-gray-800 mb-2 text-sm sm:text-base">Security Best Practices for Users</h5>
            <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
              <li>• Use strong, unique passwords for your accounts</li>
              <li>• Enable two-factor authentication when available</li>
              <li>• Keep your OAuth provider account (Google, etc.) secure</li>
              <li>• Log out from shared or public devices</li>
              <li>• Report suspicious activity immediately</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: "user-rights",
      title: "6. Your Privacy Rights and Controls",
      icon: UserCheck,
      color: "text-indigo-600",
      content: (
        <div className="space-y-4 sm:space-y-6">
          <p className="text-gray-700 text-sm sm:text-base">
            You have comprehensive rights regarding your personal data. We provide easy-to-use tools 
            and processes to exercise these rights.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                <h5 className="font-semibold text-blue-600 mb-2 flex items-center gap-2 text-sm sm:text-base">
                  <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                  Access Rights
                </h5>
                <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
                  <li>• View all personal data we have about you</li>
                  <li>• Download your data in portable format</li>
                  <li>• Access your workflow history and settings</li>
                  <li>• Review data sharing and processing activities</li>
                </ul>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
                <h5 className="font-semibold text-green-600 mb-2 flex items-center gap-2 text-sm sm:text-base">
                  <Settings className="w-3 h-3 sm:w-4 sm:h-4" />
                  Control Rights
                </h5>
                <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
                  <li>• Update or correct your personal information</li>
                  <li>• Manage privacy and communication preferences</li>
                  <li>• Control data used for AI model improvements</li>
                  <li>• Revoke permissions for integrations</li>
                </ul>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 sm:p-4">
                <h5 className="font-semibold text-orange-600 mb-2 flex items-center gap-2 text-sm sm:text-base">
                  <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                  Portability Rights
                </h5>
                <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
                  <li>• Export your workflows and data</li>
                  <li>• Receive data in machine-readable format</li>
                  <li>• Transfer data to other services</li>
                  <li>• Backup your automation configurations</li>
                </ul>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
                <h5 className="font-semibold text-red-600 mb-2 flex items-center gap-2 text-sm sm:text-base">
                  <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                  Deletion Rights
                </h5>
                <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
                  <li>• Delete specific workflows or data</li>
                  <li>• Request complete account deletion</li>
                  <li>• Remove data from AI training datasets</li>
                  <li>• Withdraw consent for data processing</li>
                </ul>
              </div>
            </div>
          </div>

          <h4 className="font-semibold text-gray-900 mt-4 sm:mt-6 text-base sm:text-lg">How to Exercise Your Rights</h4>
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 sm:p-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
              <div>
                <Settings className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-600 mx-auto mb-2" />
                <h6 className="font-semibold text-indigo-800 text-sm">Account Settings</h6>
                <p className="text-xs text-indigo-700">Most controls available in your dashboard</p>
              </div>
              <div>
                <Mail className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-600 mx-auto mb-2" />
                <h6 className="font-semibold text-indigo-800 text-sm">Email Request</h6>
                <p className="text-xs text-indigo-700">Contact privacy@tuly.app for assistance</p>
              </div>
              <div>
                <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-600 mx-auto mb-2" />
                <h6 className="font-semibold text-indigo-800 text-sm">Response Time</h6>
                <p className="text-xs text-indigo-700">We respond within 30 days</p>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3 sm:p-4 rounded-r-lg">
            <p className="text-yellow-800 text-xs sm:text-sm">
              <strong>Note:</strong> Some data deletion requests may impact service functionality. 
              We'll explain any limitations before processing your request.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "international-transfers",
      title: "7. International Data Transfers",
      icon: Globe,
      color: "text-teal-600",
      content: (
        <div className="space-y-4 sm:space-y-6">
          <p className="text-gray-700 text-sm sm:text-base">
            Tuly.app operates globally and may transfer your data internationally to provide our services. 
            We ensure all international transfers meet strict privacy and security standards.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
              <h5 className="font-semibold text-blue-600 mb-3 text-sm sm:text-base">Data Locations</h5>
              <ul className="text-xs sm:text-sm text-gray-700 space-y-2">
                <li>• Primary servers: United States (AWS)</li>
                <li>• Backup locations: European Union</li>
                <li>• CDN: Global edge locations</li>
                <li>• Analytics: Anonymized, global processing</li>
              </ul>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
              <h5 className="font-semibold text-green-600 mb-3 text-sm sm:text-base">Transfer Safeguards</h5>
              <ul className="text-xs sm:text-sm text-gray-700 space-y-2">
                <li>• Standard Contractual Clauses (SCCs)</li>
                <li>• Adequacy decisions where applicable</li>
                <li>• Data Processing Agreements (DPAs)</li>
                <li>• Regular compliance audits</li>
              </ul>
            </div>
          </div>

          <h4 className="font-semibold text-gray-900 mt-4 sm:mt-6 text-base sm:text-lg">Regional Compliance</h4>
          <div className="space-y-3">
            <div className="border-l-4 border-blue-500 pl-3 sm:pl-4">
              <h5 className="font-semibold text-blue-700 text-sm sm:text-base">European Union (GDPR)</h5>
              <p className="text-xs sm:text-sm text-gray-700 mt-1">
                Full compliance with GDPR requirements, including data subject rights, 
                lawful basis for processing, and privacy by design principles.
              </p>
            </div>

            <div className="border-l-4 border-green-500 pl-3 sm:pl-4">
              <h5 className="font-semibold text-green-700 text-sm sm:text-base">California (CCPA/CPRA)</h5>
              <p className="text-xs sm:text-sm text-gray-700 mt-1">
                Compliance with California Consumer Privacy Act and California Privacy Rights Act, 
                including consumer rights and opt-out mechanisms.
              </p>
            </div>

            <div className="border-l-4 border-purple-500 pl-3 sm:pl-4">
              <h5 className="font-semibold text-purple-700 text-sm sm:text-base">Other Jurisdictions</h5>
              <p className="text-xs sm:text-sm text-gray-700 mt-1">
                We monitor and comply with privacy laws in all regions where we operate, 
                adapting our practices as regulations evolve.
              </p>
            </div>
          </div>

          <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 sm:p-4">
            <p className="text-teal-800 text-xs sm:text-sm">
              <strong>Data Residency Options:</strong> Enterprise customers may request specific 
              data residency arrangements. Contact our sales team for more information.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "retention",
      title: "8. Data Retention and Deletion",
      icon: Clock,
      color: "text-orange-600",
      content: (
        <div className="space-y-4 sm:space-y-6">
          <h4 className="font-semibold text-gray-900 text-base sm:text-lg">Retention Periods</h4>
          
          <div className="space-y-3">
            <div className="border rounded-lg p-3 sm:p-4">
              <h5 className="font-semibold text-blue-600 mb-2 text-sm sm:text-base">Account Data</h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm">
                <div>
                  <span className="font-medium text-gray-700">Active accounts:</span>
                  <span className="text-gray-600 ml-1 sm:ml-2 block sm:inline">Retained while account is active</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Deleted accounts:</span>
                  <span className="text-gray-600 ml-1 sm:ml-2 block sm:inline">30 days for recovery, then permanent deletion</span>
                </div>
              </div>
            </div>

            <div className="border rounded-lg p-3 sm:p-4">
              <h5 className="font-semibold text-green-600 mb-2 text-sm sm:text-base">Workflow Data</h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm">
                <div>
                  <span className="font-medium text-gray-700">Generated workflows:</span>
                  <span className="text-gray-600 ml-1 sm:ml-2 block sm:inline">Retained while account is active</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Deleted workflows:</span>
                  <span className="text-gray-600 ml-1 sm:ml-2 block sm:inline">Immediately removed from your account</span>
                </div>
              </div>
            </div>

            <div className="border rounded-lg p-3 sm:p-4">
              <h5 className="font-semibold text-purple-600 mb-2 text-sm sm:text-base">Analytics Data</h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm">
                <div>
                  <span className="font-medium text-gray-700">Usage analytics:</span>
                  <span className="text-gray-600 ml-1 sm:ml-2 block sm:inline">26 months maximum</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Performance logs:</span>
                  <span className="text-gray-600 ml-1 sm:ml-2 block sm:inline">90 days maximum</span>
                </div>
              </div>
            </div>

            <div className="border rounded-lg p-3 sm:p-4">
              <h5 className="font-semibold text-orange-600 mb-2 text-sm sm:text-base">Legal & Compliance</h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm">
                <div>
                  <span className="font-medium text-gray-700">Financial records:</span>
                  <span className="text-gray-600 ml-1 sm:ml-2 block sm:inline">7 years (legal requirement)</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Support tickets:</span>
                  <span className="text-gray-600 ml-1 sm:ml-2 block sm:inline">3 years maximum</span>
                </div>
              </div>
            </div>
          </div>

          <h4 className="font-semibold text-gray-900 mt-4 sm:mt-6 text-base sm:text-lg">Automated Deletion</h4>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 sm:p-4">
            <ul className="text-xs sm:text-sm text-gray-700 space-y-2">
              <li>• Inactive free accounts: Deleted after 2 years of inactivity</li>
              <li>• Temporary files: Deleted within 24 hours</li>
              <li>• Session data: Deleted upon logout or expiration</li>
              <li>• Failed payment accounts: 60 days grace period, then deletion</li>
              <li>• GDPR/CCPA deletion requests: Processed within 30 days</li>
            </ul>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-3 sm:p-4 rounded-r-lg">
            <p className="text-blue-800 text-xs sm:text-sm">
              <strong>Data Export Before Deletion:</strong> We strongly recommend exporting your 
              workflows and data before account deletion, as this process is irreversible.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "cookies",
      title: "9. Cookies and Tracking Technologies",
      icon: Cookie,
      color: "text-pink-600",
      content: (
        <div className="space-y-4 sm:space-y-6">
          <p className="text-gray-700 text-sm sm:text-base">
            We use cookies and similar technologies to enhance your experience, analyze usage, 
            and provide personalized content. You have control over these technologies.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
              <h5 className="font-semibold text-green-600 mb-3 text-sm sm:text-base">Essential Cookies</h5>
              <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
                <li>• Authentication and session management</li>
                <li>• Security and fraud prevention</li>
                <li>• Basic functionality and preferences</li>
                <li>• Load balancing and performance</li>
              </ul>
              <div className="text-xs text-green-600 mt-2 font-medium">Always Active</div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
              <h5 className="font-semibold text-blue-600 mb-3 text-sm sm:text-base">Analytics Cookies</h5>
              <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
                <li>• Usage patterns and feature adoption</li>
                <li>• Performance monitoring</li>
                <li>• Error tracking and diagnostics</li>
                <li>• A/B testing and optimization</li>
              </ul>
              <div className="text-xs text-blue-600 mt-2 font-medium">Can Be Disabled</div>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 sm:p-4">
              <h5 className="font-semibold text-purple-600 mb-3 text-sm sm:text-base">Functional Cookies</h5>
              <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
                <li>• UI preferences and settings</li>
                <li>• Language and region preferences</li>
                <li>• Workflow templates and favorites</li>
                <li>• Integration configurations</li>
              </ul>
              <div className="text-xs text-purple-600 mt-2 font-medium">Can Be Disabled</div>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 sm:p-4">
              <h5 className="font-semibold text-orange-600 mb-3 text-sm sm:text-base">Marketing Cookies</h5>
              <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
                <li>• Personalized content recommendations</li>
                <li>• Email campaign effectiveness</li>
                <li>• Social media integration</li>
                <li>• Referral tracking</li>
              </ul>
              <div className="text-xs text-orange-600 mt-2 font-medium">Opt-In Required</div>
            </div>
          </div>

          <h4 className="font-semibold text-gray-900 mt-4 sm:mt-6 text-base sm:text-lg">Third-Party Technologies</h4>
          <div className="space-y-3">
            <div className="border-l-4 border-blue-500 pl-3 sm:pl-4">
              <h5 className="font-semibold text-blue-700 text-sm sm:text-base">Google Analytics</h5>
              <p className="text-xs sm:text-sm text-gray-700 mt-1">
                We use Google Analytics with IP anonymization to understand user behavior and improve our service.
              </p>
            </div>

            <div className="border-l-4 border-green-500 pl-3 sm:pl-4">
              <h5 className="font-semibold text-green-700 text-sm sm:text-base">Intercom</h5>
              <p className="text-xs sm:text-sm text-gray-700 mt-1">
                Customer support chat functionality to provide real-time assistance.
              </p>
            </div>

            <div className="border-l-4 border-purple-500 pl-3 sm:pl-4">
              <h5 className="font-semibold text-purple-700 text-sm sm:text-base">Mixpanel</h5>
              <p className="text-xs sm:text-sm text-gray-700 mt-1">
                Product analytics to understand feature usage and improve user experience.
              </p>
            </div>
          </div>

          <div className="bg-pink-50 border border-pink-200 rounded-lg p-3 sm:p-4">
            <h5 className="font-semibold text-pink-600 mb-2 text-sm sm:text-base">Cookie Controls</h5>
            <p className="text-xs sm:text-sm text-gray-700 mb-3">
              You can manage cookie preferences through your browser settings or our cookie preference center:
            </p>
            <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
              <li>• Access cookie settings in your account preferences</li>
              <li>• Use browser controls to block or delete cookies</li>
              <li>• Opt out of specific tracking technologies</li>
              <li>• Configure cookie expiration preferences</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: "children-privacy",
      title: "10. Children's Privacy",
      icon: Shield,
      color: "text-red-500",
      content: (
        <div className="space-y-4 sm:space-y-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
            <h4 className="font-semibold text-red-800 mb-2 text-sm sm:text-base">Age Restriction</h4>
            <p className="text-red-700 text-xs sm:text-sm">
              Tuly.app is designed for business and professional use and is not intended for children under 13 years of age. 
              We do not knowingly collect personal information from children under 13.
            </p>
          </div>

          <div className="space-y-3">
            <div className="border-l-4 border-orange-500 pl-3 sm:pl-4">
              <h5 className="font-semibold text-orange-700 text-sm sm:text-base">If You Are Under 18</h5>
              <p className="text-xs sm:text-sm text-gray-700 mt-1">
                If you are under 18, you must have permission from a parent or guardian to use our service. 
                We recommend parental supervision for all users under 18.
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-3 sm:pl-4">
              <h5 className="font-semibold text-blue-700 text-sm sm:text-base">Parental Rights</h5>
              <p className="text-xs sm:text-sm text-gray-700 mt-1">
                Parents and guardians have the right to review, modify, or delete their child's personal information. 
                Contact us at privacy@tuly.app for assistance.
              </p>
            </div>

            <div className="border-l-4 border-green-500 pl-3 sm:pl-4">
              <h5 className="font-semibold text-green-700 text-sm sm:text-base">Accidental Collection</h5>
              <p className="text-xs sm:text-sm text-gray-700 mt-1">
                If we discover we have collected information from a child under 13, we will delete it immediately. 
                Please contact us if you believe we may have collected such information.
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "policy-changes",
      title: "11. Changes to This Privacy Policy",
      icon: Bell,
      color: "text-yellow-600",
      content: (
        <div className="space-y-4 sm:space-y-6">
          <p className="text-gray-700 text-sm sm:text-base">
            We may update this Privacy Policy from time to time to reflect changes in our practices, 
            technology, legal requirements, or business operations.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
              <h5 className="font-semibold text-blue-600 mb-3 text-sm sm:text-base">How We Notify You</h5>
              <ul className="text-xs sm:text-sm text-gray-700 space-y-2">
                <li>• Email notification for significant changes</li>
                <li>• In-app notifications and banners</li>
                <li>• Updated "Last Modified" date on policy</li>
                <li>• Blog posts for major policy updates</li>
              </ul>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
              <h5 className="font-semibold text-green-600 mb-3 text-sm sm:text-base">Your Response Options</h5>
              <ul className="text-xs sm:text-sm text-gray-700 space-y-2">
                <li>• Continue using service (acceptance)</li>
                <li>• Update your privacy preferences</li>
                <li>• Contact us with questions or concerns</li>
                <li>• Delete your account if you disagree</li>
              </ul>
            </div>
          </div>

          <div className="space-y-3">
            <div className="border-l-4 border-yellow-500 pl-3 sm:pl-4">
              <h5 className="font-semibold text-yellow-700 text-sm sm:text-base">Minor Changes</h5>
              <p className="text-xs sm:text-sm text-gray-700 mt-1">
                For minor changes (typos, clarifications, formatting), we will update the policy 
                without additional notice beyond the updated date.
              </p>
            </div>

            <div className="border-l-4 border-orange-500 pl-3 sm:pl-4">
              <h5 className="font-semibold text-orange-700 text-sm sm:text-base">Significant Changes</h5>
              <p className="text-xs sm:text-sm text-gray-700 mt-1">
                For material changes affecting your rights or data use, we will provide 30 days' 
                advance notice via email and prominent website notices.
              </p>
            </div>

            <div className="border-l-4 border-red-500 pl-3 sm:pl-4">
              <h5 className="font-semibold text-red-700 text-sm sm:text-base">Legal Requirement Changes</h5>
              <p className="text-xs sm:text-sm text-gray-700 mt-1">
                Changes required by law may take effect immediately, with notification 
                provided as soon as reasonably possible.
              </p>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
            <p className="text-yellow-800 text-xs sm:text-sm">
              <strong>Version History:</strong> We maintain a version history of our Privacy Policy. 
              Previous versions are available upon request for your records.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "contact",
      title: "12. Contact Information",
      icon: Phone,
      color: "text-gray-600",
      content: (
        <div className="space-y-4 sm:space-y-6">
          <p className="text-gray-700 text-sm sm:text-base">
            We're here to help with any privacy-related questions or concerns. 
            Here are the best ways to reach our privacy team:
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                <h5 className="font-semibold text-blue-600 mb-3 flex items-center gap-2 text-sm sm:text-base">
                  <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
                  Privacy Team
                </h5>
                <div className="space-y-2 text-xs sm:text-sm text-gray-700">
                  <p><strong>Email:</strong> privacy@tuly.app</p>
                  <p><strong>Response time:</strong> 48 hours</p>
                  <p><strong>Languages:</strong> English, Arabic</p>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
                <h5 className="font-semibold text-green-600 mb-3 flex items-center gap-2 text-sm sm:text-base">
                  <Settings className="w-4 h-4 sm:w-5 sm:h-5" />
                  Data Protection Officer
                </h5>
                <div className="space-y-2 text-xs sm:text-sm text-gray-700">
                  <p><strong>Email:</strong> dpo@tuly.app</p>
                  <p><strong>For:</strong> GDPR inquiries, formal complaints</p>
                  <p><strong>Response time:</strong> 30 days maximum</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 sm:p-4">
                <h5 className="font-semibold text-purple-600 mb-3 flex items-center gap-2 text-sm sm:text-base">
                  <MapPin className="w-4 h-4 sm:w-5 sm:h-5" />
                  Mailing Address
                </h5>
                <div className="text-xs sm:text-sm text-gray-700">
                  <p><strong>Tuly Technologies Inc.</strong></p>
                  <p>Privacy Department</p>
                  <p>Al Nahda2, Awqaf Bld.</p>
                  <p>Dubai</p>
                  <p>United Arab Emirates</p>
                </div>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 sm:p-4">
                <h5 className="font-semibold text-orange-600 mb-3 flex items-center gap-2 text-sm sm:text-base">
                  <Scale className="w-4 h-4 sm:w-5 sm:h-5" />
                  Legal Requests
                </h5>
                <div className="space-y-2 text-xs sm:text-sm text-gray-700">
                  <p><strong>Email:</strong> legal@tuly.app</p>
                  <p><strong>For:</strong> Subpoenas, court orders, law enforcement</p>
                  <p><strong>Note:</strong> Must include proper legal documentation</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4">
            <h5 className="font-semibold text-gray-800 mb-3 text-sm sm:text-base">Before You Contact Us</h5>
            <p className="text-xs sm:text-sm text-gray-700 mb-3">
              To help us assist you more efficiently, please check these resources first:
            </p>
            <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
              <li>• Account Settings → Privacy Controls for most data management tasks</li>
              <li>• Help Center at help.tuly.app for common questions</li>
              <li>• This Privacy Policy for detailed information about our practices</li>
              <li>• Status page at status.tuly.app for service-related issues</li>
            </ul>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-3 sm:p-4 rounded-r-lg">
            <p className="text-blue-800 text-xs sm:text-sm">
              <strong>Response Commitment:</strong> We aim to respond to all privacy inquiries within 48 hours 
              and resolve most issues within 30 days. Complex requests may take longer, but we'll keep you updated 
              throughout the process.
            </p>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600 flex-shrink-0" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Privacy Policy</h1>
          </div>
          <p className="text-sm sm:text-base text-gray-600">
            Last updated: {lastUpdated} • Effective immediately
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="space-y-3 sm:space-y-4">
          {sections.map((section) => {
            const Icon = section.icon;
            const isExpanded = expandedSections[section.id];

            return (
              <div
                key={section.id}
                className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-md"
              >
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full px-4 sm:px-6 py-3 sm:py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-150"
                >
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${section.color} flex-shrink-0`} />
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                      {section.title}
                    </h2>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                  ) : (
                    <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                  )}
                </button>

                {isExpanded && (
                  <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                    <div className="border-t border-gray-100 pt-4 sm:pt-6">
                      {section.content}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="mt-8 sm:mt-12 text-center text-xs sm:text-sm text-gray-500 bg-gray-50 rounded-lg p-4 sm:p-6">
          <p>
            This Privacy Policy is part of our Terms of Service. By using Tuly.app, 
            you agree to both documents. For questions, contact us at{' '}
            <a href="mailto:privacy@tuly.app" className="text-blue-600 hover:underline break-all">
              privacy@tuly.app
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;